import { useSSEStream } from '@/hooks/useSSEStream';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SSEStreamStatus } from '@/types/sse.types';
import { Loader2, Play, Square, RotateCcw, Sparkles } from 'lucide-react';

export function StreamingPage() {
  const { state, startStream, stopStream, resetStream, isStreaming, canStart } = useSSEStream();

  const getStatusColor = () => {
    switch (state.status) {
      case SSEStreamStatus.IDLE:
        return 'text-gray-500';
      case SSEStreamStatus.CONNECTING:
        return 'text-yellow-500';
      case SSEStreamStatus.STREAMING:
        return 'text-blue-500';
      case SSEStreamStatus.COMPLETED:
        return 'text-green-500';
      case SSEStreamStatus.ERROR:
        return 'text-red-500';
      case SSEStreamStatus.ABORTED:
        return 'text-orange-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusText = () => {
    switch (state.status) {
      case SSEStreamStatus.IDLE:
        return 'Ready to stream';
      case SSEStreamStatus.CONNECTING:
        return 'Connecting...';
      case SSEStreamStatus.STREAMING:
        return 'Streaming...';
      case SSEStreamStatus.COMPLETED:
        return 'Completed';
      case SSEStreamStatus.ERROR:
        return 'Error';
      case SSEStreamStatus.ABORTED:
        return 'Aborted';
      default:
        return 'Unknown';
    }
  };

  const formatDuration = (ms: number) => {
    return (ms / 1000).toFixed(2) + 's';
  };

  return (
    <div className="min-h-screen from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Control Panel */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${
                  state.isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-300'
                }`} />
                <span className={`font-medium ${getStatusColor()}`}>
                  {getStatusText()}
                </span>
              </div>

              {typeof state.ttft === 'number' && (
                <div className="flex items-center gap-2 text-sm border-l border-gray-300 dark:border-gray-600 pl-4">
                  <span className="text-gray-500 dark:text-gray-400">TTFT:</span>
                  <span className="font-semibold text-purple-600 dark:text-purple-400">
                    {state.ttft}ms
                  </span>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={startStream}
                disabled={!canStart || isStreaming}
                className="gap-2"
              >
                {state.status === SSEStreamStatus.CONNECTING ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Connecting
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Start Stream
                  </>
                )}
              </Button>

              <Button
                onClick={stopStream}
                disabled={!isStreaming}
                variant="destructive"
                className="gap-2"
              >
                <Square className="w-4 h-4" />
                Stop
              </Button>

              <Button
                onClick={resetStream}
                disabled={state.status === SSEStreamStatus.IDLE}
                variant="outline"
                className="gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </Button>
            </div>
          </div>

          {/* Metadata */}
          {state.metadata && (
            <div className="grid grid-cols-4 gap-4 pt-4 border-t">
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">TTFT</p>
                <p className="text-2xl font-bold text-orange-600">
                  {state.ttft !== null && state.ttft !== undefined ? `${state.ttft}ms` : 'N/A'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Chunks</p>
                <p className="text-2xl font-bold text-purple-600">
                  {state.metadata.totalChunks}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Duration</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatDuration(state.metadata.totalDuration)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Characters</p>
                <p className="text-2xl font-bold text-green-600">
                  {state.metadata.totalCharacters}
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Streaming Content */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Streamed Content</h2>
              <span className="text-sm text-gray-500">
                {state.content.length} characters
              </span>
            </div>

            <div className="relative">
              {state.content ? (
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <div className="whitespace-pre-wrap bg-gray-50 dark:bg-gray-800 rounded-lg p-4 min-h-[300px] max-h-[500px] overflow-y-auto font-mono text-sm">
                    {state.content}
                    {isStreaming && (
                      <span className="inline-block w-2 h-4 bg-blue-500 animate-pulse ml-1" />
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[300px] text-gray-400">
                  <Sparkles className="w-12 h-12 mb-4 opacity-50" />
                  <p>Click "Start Stream" to begin streaming content</p>
                </div>
              )}
            </div>

            {/* Error Display */}
            {state.error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-600 dark:text-red-400 font-medium">
                  Error: {state.error}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Debug Info */}
        {state.chunks.length > 0 && (
          <Card className="p-6">
            <details className="cursor-pointer">
              <summary className="font-semibold text-gray-700 dark:text-gray-300">
                Debug Info ({state.chunks.length} chunks)
              </summary>
              <div className="mt-4 space-y-2 max-h-[200px] overflow-y-auto">
                {state.chunks.slice(-10).map((chunk, idx) => (
                  <div
                    key={idx}
                    className="text-xs bg-gray-50 dark:bg-gray-800 rounded p-2 font-mono"
                  >
                    <span className="text-gray-500">#{chunk.index}</span>
                    {' | '}
                    <span className="text-blue-600">{chunk.text}</span>
                    {' | '}
                    <span className="text-gray-400">
                      {chunk.duration ? `${chunk.duration}ms` : 'first'}
                    </span>
                  </div>
                ))}
                {state.chunks.length > 10 && (
                  <p className="text-xs text-gray-500 text-center">
                    Showing last 10 chunks
                  </p>
                )}
              </div>
            </details>
          </Card>
        )}
      </div>
    </div>
  );
}
